import React, { useState, useEffect } from 'react';
import { useApp } from '../pages/providers/AppProvider';
import { trainingService } from '@/services/trainingService';
import { normalizeMediaUrl } from '@/utils/media';
import Loader from '@/components/ui/Loader';
import {
    Calendar,
    Clock,
    DollarSign,
    User,
    AlertCircle,
    Building2,
    Users,
    CheckCircle2,
    MessageCircle,
    ArrowLeft,
    ChevronRight,
    Search,
    CreditCard,
    ArrowRight,
    Wallet
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import CurrencyRupeeNepaleseIcon from '@/components/ui/CurrencyRupeeNepaleseIcon';
import { toast } from 'sonner';

export function TrainingPage() {
    const { theme, siteSettings } = useApp();
    const [trainings, setTrainings] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    // Page View State: 'list' | 'enroll'
    const [view, setView] = useState<'list' | 'enroll'>('list');
    const [selectedTraining, setSelectedTraining] = useState<any>(null);
    const [enrollStep, setEnrollStep] = useState(1); // 1: Form, 2: Review, 3: Payment
    const [isSubmitting, setIsSubmitting] = useState(false);

    const [enrollForm, setEnrollForm] = useState({
        organizationName: '',
        participantCount: 1
    });

    useEffect(() => {
        const fetchTrainings = async () => {
            try {
                const res = await trainingService.getAll();
                if (Array.isArray(res)) {
                    const activeTrainings = res.filter((t: any) =>
                        t.status === 'active' || t.status === 'upcoming'
                    );
                    setTrainings(activeTrainings);
                }
            } catch (err) {
                console.error('Failed to load trainings:', err);
                setError('Failed to load training programs. Please try again later.');
            } finally {
                setLoading(false);
            }
        };

        fetchTrainings();
    }, []);

    const handleEnrollClick = (training: any) => {
        setSelectedTraining(training);
        setEnrollStep(1);
        setEnrollForm({
            organizationName: '',
            participantCount: 1
        });
        setView('enroll');
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleBackToTrainings = () => {
        setView('list');
        setSelectedTraining(null);
    };

    const handleEnrollSubmit = async () => {
        if (!enrollForm.organizationName.trim()) {
            toast.error('Organization name is required');
            return;
        }

        if (enrollStep === 1) {
            setEnrollStep(2);
            return;
        }

        if (enrollStep === 2) {
            setIsSubmitting(true);
            try {
                await trainingService.enroll(selectedTraining.id || selectedTraining._id, enrollForm);
                setEnrollStep(3);
                window.scrollTo({ top: 0, behavior: 'smooth' });
            } catch (err: any) {
                console.error('Enrollment failed:', err);
                toast.error(err.message || 'Enrollment request failed. Please try again.');
            } finally {
                setIsSubmitting(false);
            }
        }
    };

    const calculateFeeDetails = (training: any = selectedTraining, count: number = enrollForm.participantCount) => {
        if (!training) return { subtotal: 0, vatAmount: 0, total: 0, vatPercentage: 0 };
        const priceNum = parseFloat(String(training.price).replace(/[^0-9.]/g, '') || '0');
        const subtotal = priceNum * count;
        const vatPercentage = training.taxPolicy === 'excluding' ? (parseFloat(training.vatPercentage) || 0) : 0;
        const vatAmount = (subtotal * vatPercentage) / 100;
        return {
            subtotal,
            vatPercentage,
            vatAmount,
            total: subtotal + vatAmount
        };
    };

    if (loading) {
        return <Loader text="Loading Training Programs..." />;
    }

    if (error) {
        return (
            <div className="min-h-[50vh] flex flex-col items-center justify-center p-8">
                <AlertCircle className="h-12 w-12 text-red-500 mb-4" />
                <p className="text-xl text-gray-600">{error}</p>
            </div>
        );
    }

    // --- STEPPER COMPONENT ---
    const Stepper = ({ currentStep }: { currentStep: number }) => {
        const steps = [
            { id: 1, name: 'Details', icon: Building2 },
            { id: 2, name: 'Review', icon: CheckCircle2 },
            { id: 3, name: 'Payment', icon: Wallet },
        ];

        return (
            <div className="flex items-center justify-center w-full max-w-2xl mx-auto mb-12">
                {steps.map((step, idx) => (
                    <React.Fragment key={step.id}>
                        <div className="flex flex-col items-center relative">
                            <div className={`w-12 h-12 rounded-full flex items-center justify-center transition-all duration-500 border-2 ${currentStep >= step.id
                                ? 'bg-blue-600 border-blue-600 text-white shadow-lg shadow-blue-200'
                                : 'bg-white border-gray-200 text-gray-400'
                                }`}>
                                <step.icon className="h-6 w-6" />
                            </div>
                            <span className={`absolute -bottom-7 text-xs font-bold uppercase tracking-wider ${currentStep >= step.id ? 'text-blue-600' : 'text-gray-400'
                                }`}>
                                {step.name}
                            </span>
                        </div>
                        {idx !== steps.length - 1 && (
                            <div className={`flex-1 h-1 mx-4 rounded-full transition-all duration-500 ${currentStep > step.id ? 'bg-blue-600' : 'bg-gray-200'
                                }`} />
                        )}
                    </React.Fragment>
                ))}
            </div>
        );
    };

    return (
        <div className={`min-h-screen pb-20 pt-10 ${theme === 'dark' ? 'bg-gray-900 text-white' : 'bg-gray-50 text-gray-900'}`}>
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

                {view === 'list' ? (
                    <>
                        <div className="text-center mb-12 animate-in fade-in slide-in-from-top-4 duration-700">
                            <h1 className="text-4xl font-extrabold mb-4 tracking-tight">Professional Training Programs</h1>
                            <p className="text-xl text-gray-600 dark:text-gray-300">Unlock your potential with our expert-led sessions</p>
                            <div className="mt-6 w-24 h-1.5 bg-blue-600 mx-auto rounded-full" />
                        </div>

                        {trainings.length === 0 ? (
                            <div className="text-center py-20 bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-100 dark:border-gray-700">
                                <Calendar className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                                <h3 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Coming Soon!</h3>
                                <p className="text-gray-500 mt-2">New training sessions are being prepared. Stay tuned.</p>
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                                {trainings.map((training, index) => (
                                    <div
                                        key={training.id || training._id}
                                        className="bg-white dark:bg-gray-800 rounded-xl shadow-sm overflow-hidden hover:shadow-xl transition-all duration-300 flex flex-col h-full border border-gray-100 dark:border-gray-700 group animate-in fade-in slide-in-from-bottom-4"
                                        style={{ animationDelay: `${index * 50}ms` }}
                                    >
                                        <div className="h-40 overflow-hidden relative">
                                            {training.image ? (
                                                <img
                                                    src={normalizeMediaUrl(training.image)}
                                                    alt={training.title}
                                                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                                                />
                                            ) : (
                                                <div className="w-full h-full flex items-center justify-center bg-blue-50 dark:bg-gray-700">
                                                    <Calendar className="h-10 w-10 text-blue-200" />
                                                </div>
                                            )}
                                            <div className="absolute top-3 right-3">
                                                <span className={`px-2 py-0.5 text-[10px] font-black uppercase tracking-widest rounded-md shadow-sm ${training.status === 'upcoming' ? 'bg-white/90 text-blue-600 backdrop-blur-sm' : 'bg-green-500 text-white'
                                                    }`}>
                                                    {training.status}
                                                </span>
                                            </div>
                                        </div>

                                        <div className="p-4 flex-1 flex flex-col">
                                            <h3 className="text-base font-black mb-1 line-clamp-2 leading-tight group-hover:text-blue-600 transition-colors uppercase min-h-[40px]">
                                                {training.title}
                                            </h3>

                                            <div className="flex items-center gap-1.5 mb-3 text-[11px] font-bold text-gray-500 truncate">
                                                <User className="h-3 w-3 text-blue-500" />
                                                <span>{training.instructor}</span>
                                            </div>

                                            <div className="space-y-2 mb-4 bg-gray-50/50 dark:bg-gray-900/30 p-2.5 rounded-lg border border-gray-100 dark:border-gray-700">
                                                <div className="flex items-center justify-between text-[11px]">
                                                    <span className="text-gray-500 font-bold uppercase">Start</span>
                                                    <span className="font-extrabold">{new Date(training.startDate).toLocaleDateString(undefined, {
                                                        month: 'short', day: 'numeric'
                                                    })}</span>
                                                </div>
                                                <div className="flex items-center justify-between text-[11px]">
                                                    <span className="text-gray-500 font-bold uppercase">Duration</span>
                                                    <span className="font-extrabold">{training.duration}</span>
                                                </div>
                                            </div>

                                            <div className="mt-auto pt-2 border-t border-gray-100 dark:border-gray-700">
                                                <div className="flex items-end justify-between mb-3">
                                                    <div className="flex flex-col">
                                                        <span className="text-[10px] font-black text-gray-400 uppercase tracking-tighter leading-none">Price</span>
                                                        <span className="text-xl font-bold text-blue-600">
                                                            NPR {calculateFeeDetails(training, 1).subtotal.toLocaleString()}
                                                        </span>
                                                    </div>
                                                    <span className="text-[9px] text-gray-400 uppercase font-black mb-1">
                                                        {(training.taxPolicy === 'excluding') ? 'Excl. Tax' : 'Incl. Tax'}
                                                    </span>
                                                </div>

                                                <Button
                                                    onClick={() => handleEnrollClick(training)}
                                                    className="w-full bg-black hover:bg-blue-700 text-white font-black h-10 rounded-lg text-xs uppercase tracking-widest transition-all group/btn"
                                                >
                                                    Enroll
                                                    <ChevronRight className="ml-1 h-3 w-3 transform group-hover/btn:translate-x-1 transition-transform" />
                                                </Button>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </>
                ) : (
                    // --- ENROLLMENT VIEW (WINDOW FIT) ---
                    <div className="max-w-4xl mx-auto animate-in fade-in zoom-in-95 duration-500">
                        <Button
                            variant="ghost"
                            onClick={handleBackToTrainings}
                            className="mb-8 font-bold flex items-center gap-2 group hover:text-blue-600 transition-colors"
                        >
                            <ArrowLeft className="h-4 w-4 transform group-hover:-translate-x-1 transition-transform" />
                            Back to Programs
                        </Button>

                        <div className="bg-white dark:bg-gray-800 rounded-[2rem] shadow-2xl p-8 md:p-12 border border-gray-100 dark:border-gray-700 relative overflow-hidden">
                            {/* Decorative element */}
                            <div className="absolute top-0 right-0 w-32 h-32 bg-blue-600 opacity-[0.03] rounded-full -mr-16 -mt-16" />

                            <Stepper currentStep={enrollStep} />

                            <div className="mt-8">
                                {enrollStep === 1 && (
                                    <div className="space-y-8 animate-in slide-in-from-right-4 duration-500">
                                        <div className="text-center md:text-left">
                                            <h2 className="text-3xl font-black mb-2 uppercase tracking-tight">Enrollment Details</h2>
                                            <p className="text-gray-500">Please provide your organization details to proceed.</p>
                                        </div>

                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                            <div className="p-6 bg-blue-50 dark:bg-blue-900/10 rounded-2xl flex items-start gap-4">
                                                <div className="p-3 bg-white dark:bg-gray-800 rounded-xl shadow-sm">
                                                    <Building2 className="h-6 w-6 text-blue-600" />
                                                </div>
                                                <div>
                                                    <h3 className="font-black text-blue-900 dark:text-blue-300 uppercase text-sm tracking-widest">Selected Course</h3>
                                                    <p className="font-bold text-gray-900 dark:text-white mt-1 text-lg leading-tight">{selectedTraining?.title}</p>
                                                    <p className="text-sm text-blue-600 mt-1 font-medium italic">with {selectedTraining?.instructor}</p>
                                                </div>
                                            </div>

                                            <div className="space-y-6">
                                                <div className="space-y-2">
                                                    <Label htmlFor="orgName" className="font-black uppercase text-xs tracking-widest text-gray-500">Organization Name *</Label>
                                                    <Input
                                                        id="orgName"
                                                        placeholder="e.g. Acme Corporation"
                                                        value={enrollForm.organizationName}
                                                        onChange={(e) => setEnrollForm({ ...enrollForm, organizationName: e.target.value })}
                                                        className="h-14 rounded-xl border-2 focus-visible:ring-blue-600 font-bold"
                                                    />
                                                </div>
                                                <div className="space-y-2">
                                                    <Label htmlFor="participants" className="font-black uppercase text-xs tracking-widest text-gray-500">Number of Participants *</Label>
                                                    <div className="relative">
                                                        <Input
                                                            id="participants"
                                                            type="number"
                                                            min="1"
                                                            value={enrollForm.participantCount}
                                                            onChange={(e) => setEnrollForm({ ...enrollForm, participantCount: parseInt(e.target.value) || 1 })}
                                                            className="h-14 rounded-xl border-2 pl-12 font-black text-lg focus-visible:ring-blue-600"
                                                        />
                                                        <Users className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="pt-8 flex justify-end">
                                            <Button
                                                onClick={handleEnrollSubmit}
                                                className="h-16 px-10 bg-black hover:bg-blue-700 text-white font-black text-xl rounded-2xl shadow-xl uppercase tracking-widest group"
                                            >
                                                Next Step
                                                <ArrowRight className="ml-3 h-6 w-6 transform group-hover:translate-x-1 transition-transform" />
                                            </Button>
                                        </div>
                                    </div>
                                )}

                                {enrollStep === 2 && (
                                    <div className="space-y-8 animate-in slide-in-from-right-4 duration-500">
                                        <div className="text-center md:text-left">
                                            <h2 className="text-3xl font-black mb-2 uppercase tracking-tight">Review Enrollment</h2>
                                            <p className="text-gray-500">Verify your details and the calculated fee before proceeding.</p>
                                        </div>

                                        <Card className="border-2 border-gray-100 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-900/30 rounded-3xl overflow-hidden">
                                            <CardContent className="p-4 sm:p-8">
                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 divide-y md:divide-y-0 md:divide-x divide-gray-200 dark:divide-gray-700">
                                                    <div className="space-y-6 pb-8 md:pb-0 md:pr-8">
                                                        <div>
                                                            <h4 className="text-xs font-black uppercase tracking-[0.2em] text-gray-400 mb-2">Subject</h4>
                                                            <p className="text-xl font-bold">{selectedTraining?.title}</p>
                                                        </div>
                                                        <div>
                                                            <h4 className="text-xs font-black uppercase tracking-[0.2em] text-gray-400 mb-2">Organization</h4>
                                                            <p className="text-xl font-bold">{enrollForm.organizationName}</p>
                                                        </div>
                                                        <div className="flex justify-between">
                                                            <div>
                                                                <h4 className="text-xs font-black uppercase tracking-[0.2em] text-gray-400 mb-2">Quantity</h4>
                                                                <p className="text-xl font-bold">{enrollForm.participantCount} Person(s)</p>
                                                            </div>
                                                            <div className="text-right">
                                                                <h4 className="text-xs font-black uppercase tracking-[0.2em] text-gray-400 mb-2">Rate</h4>
                                                                <p className="text-xl font-bold">{selectedTraining?.price}</p>
                                                            </div>
                                                        </div>
                                                    </div>

                                                    <div className="pt-8 md:pt-0 md:pl-8 flex flex-col justify-center items-center md:items-end w-full">
                                                        <div className="w-full bg-white dark:bg-gray-800 p-5 sm:p-8 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700">
                                                            <div className="flex items-center justify-between mb-4 gap-2">
                                                                <span className="text-gray-500 font-bold uppercase tracking-widest text-[10px] sm:text-xs">Subtotal</span>
                                                                <span className="font-black text-lg sm:text-xl whitespace-nowrap">NPR {calculateFeeDetails().subtotal.toLocaleString()}</span>
                                                            </div>
                                                            {calculateFeeDetails().vatPercentage > 0 && (
                                                                <div className="flex items-center justify-between mb-4 gap-2">
                                                                    <span className="text-gray-500 font-bold uppercase tracking-widest text-[10px] sm:text-xs">VAT ({calculateFeeDetails().vatPercentage}%)</span>
                                                                    <span className="font-black text-base sm:text-lg text-amber-600 whitespace-nowrap">+ NPR {calculateFeeDetails().vatAmount.toLocaleString()}</span>
                                                                </div>
                                                            )}
                                                            <div className="h-px bg-gray-100 dark:bg-gray-700 w-full mb-6" />
                                                            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                                                                <span className="text-blue-600 font-black uppercase tracking-[0.25em] text-xs sm:text-sm">Grand Total</span>
                                                                <div className="text-left sm:text-right w-full sm:w-auto">
                                                                    <span className="font-black text-3xl sm:text-4xl text-blue-600 whitespace-nowrap">NPR {calculateFeeDetails().total.toLocaleString()}</span>
                                                                    <p className="text-[10px] font-black text-gray-400 mt-1 uppercase">
                                                                        {selectedTraining?.taxPolicy === 'excluding' && calculateFeeDetails().vatPercentage > 0
                                                                            ? `Inclusive of ${calculateFeeDetails().vatPercentage}% VAT`
                                                                            : 'Inclusive of all taxes'}
                                                                    </p>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </CardContent>
                                        </Card>

                                        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-10">
                                            <Button
                                                variant="outline"
                                                onClick={() => setEnrollStep(1)}
                                                className="h-14 w-full sm:w-auto px-8 rounded-2xl border-2 font-black uppercase tracking-widest flex items-center justify-center gap-2 order-2 sm:order-1"
                                            >
                                                <ArrowLeft className="h-4 w-4" /> Edit
                                            </Button>
                                            <Button
                                                onClick={handleEnrollSubmit}
                                                disabled={isSubmitting}
                                                className="h-16 w-full sm:w-auto px-6 sm:px-10 bg-blue-600 hover:bg-blue-700 text-white font-black text-lg sm:text-xl rounded-2xl shadow-xl uppercase tracking-widest order-1 sm:order-2"
                                            >
                                                {isSubmitting ? 'Processing...' : 'Confirm & Pay'}
                                                {!isSubmitting && <ChevronRight className="ml-3 h-6 w-6" />}
                                            </Button>
                                        </div>
                                    </div>
                                )}

                                {enrollStep === 3 && (
                                    <div className="space-y-10 animate-in zoom-in-95 duration-700 text-center max-w-2xl mx-auto">
                                        <div>
                                            <div className="w-24 h-24 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-6 shadow-xl shadow-green-100 dark:shadow-none animate-bounce">
                                                <CheckCircle2 className="h-14 w-14 text-green-600" />
                                            </div>
                                            <h2 className="text-4xl font-black mb-3 uppercase tracking-tight">Success! Enrollment Registered</h2>
                                            <p className="text-gray-500 text-lg">Your enrollment for <span className="text-blue-600 font-black uppercase tracking-widest">{selectedTraining?.title}</span> has been received. Please follow the final steps below.</p>
                                        </div>

                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center bg-gray-50 dark:bg-gray-900/50 p-10 rounded-[2.5rem] border-4 border-white dark:border-gray-800 shadow-xl">
                                            <div className="space-y-4">
                                                <div className="bg-white dark:bg-gray-800 p-4 rounded-3xl shadow-2xl inline-block transform -rotate-3 hover:rotate-0 transition-transform duration-500">
                                                    {siteSettings.trainingQrCodeUrl ? (
                                                        <img
                                                            src={normalizeMediaUrl(siteSettings.trainingQrCodeUrl)}
                                                            alt="Payment QR"
                                                            className="w-full max-w-[220px] rounded-2xl"
                                                        />
                                                    ) : (
                                                        <div className="w-[200px] h-[200px] flex items-center justify-center bg-gray-100 rounded-2xl">
                                                            <CreditCard className="h-12 w-12 text-gray-300" />
                                                        </div>
                                                    )}
                                                </div>
                                                <div className="pt-2">
                                                    <span className="inline-block bg-black text-white font-black uppercase tracking-[0.2em] py-1 px-4 text-[10px] rounded-full">Scan & Pay</span>
                                                    <p className="text-xs text-gray-400 mt-2 font-bold uppercase tracking-widest">Supports Fonepay, Khalti & eSewa</p>
                                                </div>
                                            </div>

                                            <div className="space-y-8 text-left">
                                                <div className="space-y-4">
                                                    <div className="flex items-center gap-4">
                                                        <div className="w-10 h-10 rounded-xl bg-blue-600 text-white flex items-center justify-center font-black text-xl shadow-lg">1</div>
                                                        <p className="font-bold text-gray-700 dark:text-gray-300">Pay <span className="text-blue-600 font-black">NPR {calculateFeeDetails().total.toLocaleString()}</span> via QR</p>
                                                    </div>
                                                    <div className="flex items-center gap-4">
                                                        <div className="w-10 h-10 rounded-xl bg-blue-600 text-white flex items-center justify-center font-black text-xl shadow-lg">2</div>
                                                        <p className="font-bold text-gray-700 dark:text-gray-300">Take a screenshot of the confirmation</p>
                                                    </div>
                                                    <div className="flex items-center gap-4">
                                                        <div className="w-10 h-10 rounded-xl bg-blue-600 text-white flex items-center justify-center font-black text-xl shadow-lg">3</div>
                                                        <p className="font-bold text-gray-700 dark:text-gray-300">Send screenshot on WhatsApp</p>
                                                    </div>
                                                </div>

                                                <div className="h-px bg-gray-200 dark:bg-gray-700 w-full" />

                                                <a
                                                    href={`https://wa.me/${siteSettings.trainingWhatsAppNumber?.replace(/[^0-9]/g, '')}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="flex items-center justify-center gap-3 w-full py-5 bg-green-600 hover:bg-green-700 text-white rounded-2xl font-black text-xl transition-all shadow-xl hover:-translate-y-1 uppercase tracking-widest"
                                                >
                                                    <MessageCircle className="h-6 w-6 fill-white" />
                                                    WhatsApp Now
                                                </a>
                                            </div>
                                        </div>

                                        <div className="pt-6">
                                            <Button
                                                variant="ghost"
                                                onClick={handleBackToTrainings}
                                                className="font-black uppercase tracking-widest text-gray-400 hover:text-blue-600 transition-colors"
                                            >
                                                Return Home
                                            </Button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
